/**
 * Kingdom Factor - Coach Page
 * Recruitment page for experienced business leaders seeking a meaningful "second act"
 */

import { SeoHead } from "@/components/SeoHead"
import {
  ArrowRight,
  Check,
  Quote,
  Users,
  TrendingUp,
  Compass,
  Crown,
  Heart,
  Award,
  Lightbulb,
  GraduationCap,
  Repeat,
  DollarSign,
  LifeBuoy,
  MessageSquare,
} from "lucide-react"
import { ScrollIndicator } from "@/components/ui/scroll-indicator"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"

export default function Coach({ seo, settings = {} }) {
  const { scheduleCallUrl = "#" } = settings

  // Four core benefits of becoming a coach
  const benefits = [
    {
      icon: Heart,
      title: "Impact Lives",
      description:
        "Guide Christian business leaders through professional growth and spiritual development. Your experience becomes their breakthrough.",
    },
    {
      icon: TrendingUp,
      title: "Earn While You Mentor",
      description:
        "Build a flexible, meaningful income stream—part-time or full-time—by equipping the next generation of faith-driven leaders.",
    },
    {
      icon: Compass,
      title: "Follow a Proven System",
      description:
        "Access our turnkey Kingdom Factor Leadership Model—a battle-tested framework for coaching success with comprehensive training.",
    },
    {
      icon: Crown,
      title: "Lead the Leaders",
      description:
        "Mentor top-tier Christian entrepreneurs, executives, and business owners who are ready to grow and hungry for wisdom.",
    },
  ]

  // What coaches provide to members
  const services = [
    {
      title: "Personal Coaching",
      description:
        "Guide leaders in integrating professional development, personal growth, and spiritual formation.",
    },
    {
      title: "Peer Advisory Group Facilitation",
      description:
        "Lead monthly peer advisory meetings where leaders engage in honest dialogue, gain diverse perspectives, and experience meaningful accountability.",
    },
    {
      title: "Leadership Model Application",
      description:
        "Help members apply the Kingdom Factor Leadership Model, strengthening relational leadership, personal integrity, and operational excellence.",
    },
    {
      title: "Community Connection",
      description:
        "Build relationships within the local Christian business community through mentorship, encouragement, and faith-centered leadership.",
    },
  ]

  // The 8 C's - Our promise to coaches
  const eightCs = [
    {
      icon: Award,
      title: "Credible",
      description: "Proven, experienced leadership team with decades of marketplace ministry",
    },
    {
      icon: Lightbulb,
      title: "Cutting-edge",
      description: "Up-to-date content, resources, and curriculum that resonates",
    },
    {
      icon: GraduationCap,
      title: "Comprehensive",
      description: "Complete training and \"Profit Promise\" to launch your coaching practice",
    },
    {
      icon: Repeat,
      title: "Consistent",
      description: "Turnkey, repeatable model you can deploy with confidence",
    },
    {
      icon: DollarSign,
      title: "Cost-efficient",
      description: "Sustainable business model designed for coach profitability",
    },
    {
      icon: LifeBuoy,
      title: "Constant",
      description: "Ongoing professional support whenever you need guidance",
    },
    {
      icon: Users,
      title: "Community",
      description: "Collaboration and connection with fellow KF coaches",
    },
    {
      icon: MessageSquare,
      title: "Candid",
      description: "Transparent, honest communication—no hidden agendas",
    },
  ]

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="coach" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero Section - Light */}
        <section className="bg-zinc-50 pt-32 pb-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-kf-red font-semibold uppercase tracking-wider mb-6">
              For Experienced Leaders
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-zinc-900 mb-6 leading-tight">
              A PREMIER COMMUNITY OF{" "}
              <span className="text-kf-red">CHRISTIAN COACHES</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-600 mb-4 font-display tracking-wide">
              Empowering Leaders. Transforming Lives. Impacting Eternal Legacy.
            </p>
            <p className="text-lg text-zinc-500 mb-10 max-w-2xl mx-auto leading-relaxed">
              Are you ready for a life-changing career that will impact the
              Kingdom of God? Blend your decades of business expertise with
              spiritual wisdom to transform the lives of Christian leaders.
            </p>
            <div className="flex flex-col items-center gap-6">
              <Button variant="gradient" size="lg" className="w-full sm:w-auto group" asChild>
                <a href="https://events.kingdomfactor.us/becomeacoach-page" target="_blank" rel="noopener noreferrer">
                  Schedule a Call
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <ScrollIndicator className="text-zinc-400" />
            </div>
          </div>
        </section>

        {/* Benefits Section - Make Your Mark */}
        <section id="benefits" className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-kf-red text-sm uppercase tracking-[0.2em] mb-4 block">
                Why Become a Coach
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                MAKE YOUR <span className="text-kf-red">MARK</span>
              </h2>
              <p className="text-zinc-600 max-w-2xl mx-auto text-lg">
                Your business experience combined with your spiritual knowledge
                can empower other Christian leaders to reach their full
                potential.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, i) => (
                <div
                  key={i}
                  className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <benefit.icon className="w-10 h-10 text-kf-red mb-4" />
                  <h3 className="font-display text-xl text-zinc-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-zinc-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The 8 C's Promise Section */}
        <section className="bg-kf-gradient py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-white/70 font-semibold uppercase tracking-wider mb-3">
                Our Promise to You
              </p>
              <h2 className="font-display text-4xl md:text-5xl text-white mb-4">
                THE 8 C's OF <span className="text-white/80">COACHING</span>
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto text-lg">
                Every Kingdom Factor Coach receives eight core commitments designed to ensure your success.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {eightCs.map((item, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-sm p-5 rounded-lg border border-white/10 hover:bg-white/15 transition-colors"
                >
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mb-3">
                    <item.icon className="w-5 h-5 text-kf-red" />
                  </div>
                  <h3 className="font-display text-lg text-white mb-1">
                    {item.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-snug">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who Becomes a Coach */}
        <section id="about" className="bg-zinc-50 py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="text-center lg:text-left">
                <span className="font-display text-7xl text-kf-red/20">01</span>
                <h2 className="font-display text-4xl md:text-5xl mb-6 -mt-4 text-zinc-900">
                  WHO BECOMES A <span className="text-kf-red">KF COACH</span>
                </h2>
                <p className="text-zinc-600 text-lg leading-relaxed mb-4">
                  Kingdom Factor Coaches are experienced business leaders, owners,
                  coaches, consultants, or ministry leaders who have carried
                  significant responsibility — either by building businesses,
                  leading organizations, or advising executives through real-world
                  pressure — while remaining grounded in their faith.
                </p>
                <p className="text-zinc-600 text-lg leading-relaxed mb-4">
                  They understand firsthand the weight of leadership — the tension
                  between external success, internal doubt, and God's calling.
                </p>
                <p className="text-zinc-600 text-lg leading-relaxed mb-4">
                  They've navigated growth, setbacks, tough decisions, and personal
                  refinement. Now they are ready to invest their experience into
                  other Christian business leaders who desire both performance and
                  purpose.
                </p>
                <p className="text-zinc-600 text-lg leading-relaxed">
                  For many, this represents a meaningful second act — not stepping
                  away from leadership, but stepping into influence that multiplies.
                </p>
              </div>

              <div className="bg-white p-8 lg:p-12 rounded-lg shadow-md">
                <p className="text-zinc-500 text-sm uppercase tracking-wider mb-6">
                  Our Target Members
                </p>
                <p className="text-zinc-700 text-lg leading-relaxed mb-6">
                  As a KF Coach, you'll work with C-suite executives of private
                  companies, Christian entrepreneurs, presidents and CEOs,
                  ministry leaders, and innovative early adopters.
                </p>
                <p className="text-zinc-700 text-lg leading-relaxed">
                  These are leaders who want to grow their business while
                  staying true to their faith—and they need your guidance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="text-center lg:text-left">
                <span className="font-display text-7xl text-kf-red/20">02</span>
                <h2 className="font-display text-4xl md:text-5xl mb-6 -mt-4 text-zinc-900">
                  HOW KF COACHES <span className="text-kf-red">SERVE LEADERS</span>
                </h2>
                <p className="text-zinc-600 text-lg leading-relaxed mb-8">
                  Kingdom Factor Coaches help members navigate the tension
                  between public image, inner thoughts, and God's calling—guiding
                  leaders to align their decisions, leadership, and businesses
                  with their faith and purpose.
                </p>

                <div className="space-y-6 text-left">
                  {services.map((service, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <Check className="w-5 h-5 text-kf-red mt-1 flex-shrink-0" />
                      <div>
                        <p className="text-zinc-900 font-semibold text-lg">
                          {service.title}
                        </p>
                        <p className="text-zinc-600">{service.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-zinc-50 p-8 lg:p-12 rounded-lg">
                <Quote className="w-12 h-12 text-kf-red/30 mb-6" />
                <p className="text-xl text-zinc-700 leading-relaxed mb-6">
                  "Most leaders don't lack information—they lack a trusted circle
                  where they can process real decisions with people who share their
                  faith and values. That's what Kingdom Factor provides."
                </p>
                <div>
                  <p className="font-bold text-zinc-900">Kingdom Factor Coach</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-kf-gradient py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl md:text-4xl mb-4 text-white">
              ARE YOU READY TO TALK?
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Let's discuss how Kingdom Factor can help you build a meaningful
              coaching career with eternal impact.
            </p>
            <Button variant="kf-secondary" size="lg" className="group" asChild>
              <a href="https://events.kingdomfactor.us/becomeacoach-page" target="_blank" rel="noopener noreferrer">
                Schedule a Call
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>

        <Footer activePage="coach" />
      </div>
    </>
  )
}
